import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const Select = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Legend",
  maxHeight = 160,
  colors = {},
  showSwatch = true,
  showSearch = true,
  searchPlaceholder = "Search",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 220 });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, query]);

  const toggle = () => setOpen((v) => !v);
  const commit = (vals) => {
    if (onChange) onChange(vals);
  };

  const onItemToggle = (v) => {
    const set = new Set(value);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    commit(Array.from(set));
  };

  const selectAll = () => commit(options.map((o) => o.value));
  const clearAll = () => commit([]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!open) return;
      const menuEl = menuRef.current;
      const btnEl = btnRef.current;
      if (!menuEl || !btnEl) return;
      if (menuEl.contains(e.target) || btnEl.contains(e.target)) return;
      setOpen(false);
    };
    const onDocKeyDown = (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 220;
    let left = rect.right - width;
    let top = rect.bottom + 6;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left < 8) left = 8;
    if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
    if (top + maxHeight + 80 > vh - 8)
      top = Math.max(8, rect.top - maxHeight - 86);
    setMenuPos({ top, left, width });
  }, [open, maxHeight]);

  useEffect(() => {
    if (!open) return;
    const handler = () => {
      const el = btnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = 220;
      let left = rect.right - width;
      let top = rect.bottom + 6;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (left < 8) left = 8;
      if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
      if (top + maxHeight + 80 > vh - 8)
        top = Math.max(8, rect.top - maxHeight - 86);
      setMenuPos({ top, left, width });
    };
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, maxHeight]);

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          background: "#fff",
          fontSize: 12,
          color: "#111827",
        }}
      >
        <span>{placeholder}</span>
        <span style={{ opacity: 0.7 }}>({value.length})</span>
      </button>
      {open &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              zIndex: 9999,
            }}
            ref={menuRef}
          >
            {showSearch && (
              <div
                style={{
                  padding: 8,
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  gap: 6,
                }}
              >
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder || "Search"}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
              </div>
            )}

            <div
              style={{
                maxHeight: maxHeight,
                overflow: "auto",
                padding: 8,
                display: "grid",
                gap: 6,
              }}
            >
              {(showSearch ? filtered : options).map((o) => {
                const active = value.includes(o.value);
                const color = colors[o.value];
                return (
                  <div
                    key={o.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => onItemToggle(o.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        onItemToggle(o.value);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      fontSize: 12,
                      padding: "6px 8px",
                      borderRadius: 6,
                      background: active ? "#eef2ff" : "transparent",
                    }}
                  >
                    {showSwatch && (
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: color || "#9ca3af",
                          display: "inline-block",
                        }}
                      />
                    )}
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {o.label}
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: 8, display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={selectAll}
                style={{
                  padding: "4px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 12,
                  background: "#f9fafb",
                }}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  padding: "4px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 12,
                  background: "#f9fafb",
                }}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  marginLeft: "auto",
                  padding: "4px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 12,
                  background: "#f9fafb",
                }}
              >
                Done
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Select;
